import * as Cesium from 'cesium'

export interface Dms { degree: number; minute: number; second: number; }

const radio_r = {
  x: 6378137,
  y: 6378137,
  z: 6356752.314245179
}

export const decimalToDms = (decimal: number): Dms => {
  const degree = Math.floor(decimal)
  const minuteDecimal = (decimal - degree) * 60
  const minute = Math.floor(minuteDecimal)
  const second = (minuteDecimal - minute) * 60
  return { degree, minute, second: parseFloat(second.toFixed(4)) }
}

export const dmsToDecimal = (dms: Dms): number => {
  return dms.degree + dms.minute / 60 + dms.second / 3600
}

// Cesium坐标类型定义
export interface CesiumCartesian3 { x: number; y: number; z: number; }
export interface CesiumCartographic { longitude: number; latitude: number; height: number; }

// ======================== Cesium辅助函数 ========================

/** * 计算两点之间的Cesium椭球体表面距离（测地线距离） */
export const calculateGeodesicDistance = (
  point1: Cesium.Cartesian3,
  point2: Cesium.Cartesian3
): number => {
  return Cesium.Cartesian3.distance(point1, point2)
}
const radiansPerDegree = Math.PI / 180.0// 角度转化为弧度(rad)
const degreesPerRadian = 180.0 / Math.PI// 弧度转化为角度
function distance (point1, point2) {
  const point1cartographic = Cesium.Cartographic.fromCartesian(point1)
  const point2cartographic = Cesium.Cartographic.fromCartesian(point2)
  /** 根据经纬度计算出距离**/
  const geodesic = new Cesium.EllipsoidGeodesic()
  geodesic.setEndPoints(point1cartographic, point2cartographic)
  let s = geodesic.surfaceDistance
  // 返回两点之间的距离
  s = Math.sqrt(Math.pow(s, 2) + Math.pow(point2cartographic.height - point1cartographic.height, 2))
  return s
}
// 计算多边形面积
function getArea (points, positions) {
  let res = 0
  // 拆分三角曲面
  for (let i = 0; i < points.length - 2; i++) {
    const j = (i + 1) % points.length
    const k = (i + 2) % points.length
    const totalAngle = Angle(points[i], points[j], points[k])
    const dis_temp1 = distance(positions[i], positions[j])
    const dis_temp2 = distance(positions[j], positions[k])
    res += dis_temp1 * dis_temp2 * Math.abs(Math.sin(totalAngle))
  }
  return (res / 1000000.0).toFixed(4)
}

/* 角度 */
function Angle (p1, p2, p3) {
  const bearing21 = Bearing(p2, p1)
  const bearing23 = Bearing(p2, p3)
  let angle = bearing21 - bearing23
  if (angle < 0) {
    angle += 360
  }
  return angle
}
/* 方向 */
function Bearing (from, to) {
  const lat1 = from.latitude * radiansPerDegree
  const lon1 = from.longitude * radiansPerDegree
  const lat2 = to.latitude * radiansPerDegree
  const lon2 = to.longitude * radiansPerDegree
  let angle = -Math.atan2(Math.sin(lon1 - lon2) * Math.cos(lat2), Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon1 - lon2))
  if (angle < 0) {
    angle += Math.PI * 2.0
  }
  angle = angle * degreesPerRadian// 角度
  return angle
}
/**
 * 计算球面多边形面积（近似算法，与原代码逻辑一致）
 * 公式：面积 = R²/2 * Σ[ (λ_{i+2} - λ_i) * sin(φ_{i+1}) ]
 * 其中 λ 为经度（弧度），φ 为纬度（弧度），索引按多边形顶点顺序循环。
 * @param {Array<{longitude: number, latitude: number}>} points - 多边形顶点数组
 * @returns {number} 面积（平方米）
 */
export function getPolygonAreaLength (points:{longitude: number, latitude: number}[]) {
  const EARTH_RADIUS = radio_r.x // 地球半径千米 (赤道半径)

  const n = points.length
  if (n < 3) return 0

  let sum = 0
  // 预转换经纬度为弧度
  const rad = points.map(item => ({
    lon: item.longitude * Math.PI / 180,
    lat: item.latitude * Math.PI / 180
  }))

  for (let i = 0; i < n; i++) {
    const a = rad[i] // 第 i 个点
    const b = rad[(i + 1) % n] // 第 i+1 个点（作为 sin 的输入）
    const c = rad[(i + 2) % n] // 第 i+2 个点（经度与 a 相减）
    sum += (c.lon - a.lon) * Math.sin(b.lat)
  }

  return Math.abs(sum) * EARTH_RADIUS * EARTH_RADIUS / 2
}

/** * 计算多边形在Cesium椭球体上的面积（测地面积） */
export const calculateCesiumPolygonArea = (positions: CesiumCartographic[]): number => {
  if (positions.length < 3) return 0
  let area = 0
  const cartographics = positions.map(pos => pos)
  area = getPolygonAreaLength(cartographics)
  console.log(area, '多边形面积')

  // const length = cartographics.length
  // for (let i = 0; i < length; i++) {
  //   const j = (i + 1) % length
  //   const k = (i + 2) % length

  //   const lon1 = Cesium.Math.toDegrees(cartographics[i].longitude)
  //   const lat1 = Cesium.Math.toDegrees(cartographics[i].latitude)
  //   const lon2 = Cesium.Math.toDegrees(cartographics[j].longitude)
  //   const lat2 = Cesium.Math.toDegrees(cartographics[j].latitude)
  //   const lon3 = Cesium.Math.toDegrees(cartographics[k].longitude)
  //   const lat3 = Cesium.Math.toDegrees(cartographics[k].latitude)

  //   const a = calcGreatCircleDistance(lat1, lon1, lat2, lon2)
  //   const b = calcGreatCircleDistance(lat2, lon2, lat3, lon3)
  //   const c = calcGreatCircleDistance(lat3, lon3, lat1, lon1)

  //   const s = (a + b + c) / 2
  //   const triangleArea = Math.sqrt(s * (s - a) * (s - b) * (s - c))

  //   area += triangleArea
  // }

  return area
}
export const calcGreatCircleDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lon2 - lon1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/** 计算航线长度 */
export const calculateCesiumPolylineLength = (positions: CesiumCartographic[]): number => {
  if (positions.length < 2) return 0
  let length = 0
  const n = positions.length
  for (let i = 0; i < n - 1; i++) {
    const point1 = Cesium.Cartesian3.fromDegrees(positions[i].longitude, positions[i].latitude, positions[i].height || 0)
    const point2 = Cesium.Cartesian3.fromDegrees(positions[i + 1].longitude, positions[i + 1].latitude, positions[i + 1].height)
    length += Cesium.Cartesian3.distance(point1, point2)
  }
  return length
}

// ======== 多边形周长 ========
export const calculateCesiumPolygonPerimeter = (positions: CesiumCartographic[]): number => {
  if (positions.length < 2) return 0
  let perimeter = 0
  const n = positions.length
  for (let i = 0; i < n; i++) {
    const point = Cesium.Cartesian3.fromDegrees(positions[i].longitude, positions[i].latitude, positions[i].height || 0)
    const point2 = Cesium.Cartesian3.fromDegrees(positions[(i + 1) % n].longitude, positions[(i + 1) % n].latitude, positions[(i + 1) % n].height || 0)
    perimeter += calculateGeodesicDistance(point, point2)
  }
  return perimeter
}
/** * 计算多边形的高度差（基于Cartographic坐标） */
export const calculateCesiumPolygonHeightDifferenceCartographic = (positions: CesiumCartographic[]): number => {
  if (positions.length < 2) return 0
  let minHeight = positions[0].height
  let maxHeight = positions[0].height
  for (let i = 1; i < positions.length; i++) {
    const height = positions[i].height
    if (height < minHeight) minHeight = height
    if (height > maxHeight) maxHeight = height
  }
  return maxHeight - minHeight
}

// ======================== Cesium圆形计算 ========================

export interface CesiumCircle { center: CesiumCartographic; radius: number; }

/** * 计算Cesium圆形的面积（基于椭球体的测地面积） */
export const calculateCesiumCircleArea = (circle: CesiumCircle): number => {
  // 近似计算圆形面积（πr²）
  return Math.PI * circle.radius * circle.radius
}

/** * 计算Cesium圆形的周长（测地周长） */
export const calculateCesiumCircleCircumference = (circle: CesiumCircle): number => {
  // 近似计算圆形周长（2πr）
  return 2 * Math.PI * circle.radius
}

/** * 生成Cesium圆形的采样点 */
export const generateCesiumCircleSamples = (circle: CesiumCircle, sampleCount: number = 36): CesiumCartographic[] => {
  const samples: CesiumCartographic[] = []
  const { center, radius } = circle

  for (let i = 0; i < sampleCount; i++) {
    const angle = (2 * Math.PI * i) / sampleCount
    // 在球面上计算点（近似）
    const lon = center.longitude + (radius / 6378137) * Math.cos(angle)
    const lat = center.latitude + (radius / 6378137) * Math.sin(angle)
    samples.push({ longitude: lon, latitude: lat, height: center.height })
  }
  return samples
}

// ======================== 与Cesium API集成的综合计算函数 ========================

/** * 计算Cesium多边形的综合信息 */
export const calculateCesiumPolygonInfo = (positions: CesiumCartographic[]): {
  area: number;
  perimeter: number;
  heightDifference: number;
  centroid?: CesiumCartographic;
} => {
  const area = calculateCesiumPolygonArea(positions)
  const perimeter = calculateCesiumPolygonPerimeter(positions)
  const heightDifference = calculateCesiumPolygonHeightDifferenceCartographic(positions)

  // 计算质心
  let sumLon = 0
  let sumLat = 0
  let sumHeight = 0
  for (const position of positions) {
    sumLon += position.longitude
    sumLat += position.latitude
    sumHeight += position.height
  }
  const centroid: CesiumCartographic = {
    longitude: sumLon / positions.length,
    latitude: sumLat / positions.length,
    height: sumHeight / positions.length
  }

  return { area, perimeter, heightDifference, centroid }
}

/** * 计算Cesium圆形的综合信息 */
export const calculateCesiumCircleInfo = (circle: CesiumCircle, sampleCount?: number): {
  area: number;
  circumference: number;
  height: number;
  samples?: CesiumCartographic[];
} => {
  const area = calculateCesiumCircleArea(circle)
  const circumference = calculateCesiumCircleCircumference(circle)
  let samples: CesiumCartographic[] | undefined

  if (sampleCount && sampleCount > 0) {
    samples = generateCesiumCircleSamples(circle, sampleCount)
  }

  return { area, circumference, height: circle.center.height, samples }
}

// ======================== 坐标转换辅助函数 ========================

/** * 将十进制度坐标转换为Cesium弧度坐标 */
export const degreesToRadians = (degrees: number): number => {
  return degrees * Math.PI / 180
}

/** * 将经纬度（十进制度）转换为Cesium Cartographic坐标 */
export const latLonToCartographic = (longitude: number, latitude: number, height: number = 0): CesiumCartographic => {
  return {
    longitude: longitude,
    latitude: latitude,
    height
  }
}

/** * 将经纬度数组转换为Cesium Cartographic数组 */
export const latLonArrayToCartographicArray = (coordinates: [number, number, number?][]): CesiumCartographic[] => {
  return coordinates.map(coord => ({
    longitude: coord[0],
    latitude: coord[1],
    height: coord[2] || 0
  }))
}

/** * 创建Cesium圆形 */
export const createCesiumCircle = (centerLon: number, centerLat: number, radius: number, height: number = 0): CesiumCircle => {
  return {
    center: latLonToCartographic(centerLon, centerLat, height),
    radius
  }
}
